/**
 * Context pour cacher la liste des utilisateurs
 *
 * ⚠️ IMPORTANT : C'est le SEUL endroit où get_users_list doit être appelé !
 *
 * Comportement :
 * - Charge les utilisateurs UNE SEULE FOIS au démarrage (si admin/superadmin)
 * - Reste en cache pour toute la session
 * - Ne recharge QUE si users est vide (après refresh manuel ou changement d'auth)
 * - Tous les hooks doivent utiliser useUsers() pour accéder au cache
 *
 * Pourquoi ?
 * - Les utilisateurs changent rarement (uniquement quand superadmin en crée/modifie)
 * - Évite des dizaines de requêtes RPC inutiles
 * - Accès instantané via usersMap (O(1) lookup)
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  type ReactNode,
} from 'react';
import { useSupabase } from '@/hooks/useSupabase';
import { useAuth } from '@/hooks/useAuth';
import { monitorQuery } from '@/lib/supabase-monitor';
import { selectUsersRpc } from './users-rpc-selector';

interface User {
  id: string;
  email: string;
  full_name: string | null;
  role?: string;
  created_at?: string;
  last_sign_in_at?: string;
  last_active_at?: string;
}

interface UsersContextValue {
  users: User[];
  usersMap: Record<string, User>;
  loading: boolean;
  /**
   * Recharge manuellement la liste des utilisateurs
   * À utiliser UNIQUEMENT après qu'un superadmin ait créé/modifié un utilisateur
   */
  refresh: () => Promise<void>;
}

const UsersContext = createContext<UsersContextValue | null>(null);

export function UsersProvider({ children }: { children: ReactNode }) {
  const supabase = useSupabase();
  const { user } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);

  // Clé pour sessionStorage (survit aux démontages/remontages)
  const USERS_CACHE_KEY = 'desertours_users_cache_loaded';

  // Mémoïser usersMap pour qu'il ne change que si users change
  const usersMap = useMemo(() => {
    return users.reduce((acc: Record<string, User>, user: User) => {
      acc[user.id] = user;
      return acc;
    }, {});
  }, [users]);

  // Mémoïser l'ID de l'utilisateur pour éviter les re-renders inutiles
  const userId = useMemo(() => user?.id, [user?.id]);

  // Mémoïser le rôle pour éviter les recalculs inutiles
  const userRole = useMemo(() => user?.app_metadata?.role, [user?.app_metadata?.role]);

  // R14 : tous les utilisateurs authentifiés peuvent récupérer la liste des
  // assignables (admin/superadmin) pour le filtre `assigned_to`. Avant cette
  // migration, seuls admin/superadmin appelaient get_users_list, donc le rôle
  // `user` (Asso) recevait usersMap = {}, rendant le filtre inutilisable
  // (commentaire client Maxime : « impossibilité pour l'asso de créer un
  // filtre par assignation »). Le choix RPC se fait dans selectUsersRpc :
  //   - admin/superadmin → get_users_list (liste complète, gated par 00009)
  //   - autres rôles    → get_assignable_users (admin/superadmin seulement,
  //                       accessible à tout authenticated, migration 00047)

  // Fonction de fetch (réutilisable pour refresh manuel)
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);

      if (!user) {
        setUsers([]);
        setLoading(false);
        return;
      }

      const rpcName = selectUsersRpc(userRole);

      const { data, error } = await monitorQuery(
        'users',
        'select',
        `${rpcName} RPC (cached)`,
        (() => supabase.rpc(rpcName)) as any,
        'UsersContext.fetchUsers'
      );

      if (!error && data) {
        const usersArray = (data as any[]).map((user: any) => ({
          id: user.id,
          email: user.email,
          full_name: user.full_name || null,
          role: user.role || 'user',
          created_at: user.created_at || undefined,
          last_sign_in_at: user.last_sign_in_at || undefined,
          last_active_at: user.last_active_at || undefined,
        }));
        setUsers(usersArray);
        // Marquer comme chargé dans sessionStorage
        sessionStorage.setItem(USERS_CACHE_KEY, 'true');
      }
    } finally {
      setLoading(false);
    }
  }, [user, userRole, supabase]);

  // Chargement initial - UNE SEULE FOIS par session
  useEffect(() => {
    // Vérifier si déjà chargé dans cette session
    const alreadyLoaded = sessionStorage.getItem(USERS_CACHE_KEY);
    if (alreadyLoaded && users.length > 0) {
      setLoading(false);
      return;
    }

    // Attendre que user soit chargé
    if (!userId) {
      setLoading(true);
      return;
    }

    // Si la liste est déjà remplie, pas besoin de recharger
    if (users.length > 0) {
      sessionStorage.setItem(USERS_CACHE_KEY, 'true');
      setLoading(false);
      return;
    }

    // R14 : tous les rôles authentifiés peuvent charger la liste assignable.
    // Le choix de la RPC est fait dans fetchUsers (admin → get_users_list,
    // autres → get_assignable_users).

    // Charger une seule fois
    fetchUsers();
    // Ne dépendre QUE de userId et userRole (valeurs stables)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, userRole]);

  return (
    <UsersContext.Provider value={{ users, usersMap, loading, refresh: fetchUsers }}>
      {children}
    </UsersContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useUsers() {
  const context = useContext(UsersContext);
  if (!context) {
    throw new Error('useUsers must be used within UsersProvider');
  }
  return context;
}
